/**
 * Admin and super-admin workflow state management.
 * Manages party lists, selection, sorting, tab navigation, and UI states.
 */

import { setState, getState, batchUpdate } from './store.js';

/**
 * Set a single admin state field.
 *
 * @param {string} field - Field name (without 'admin.' prefix)
 * @param {*} value - Value to set
 */
export function setAdminField(field, value) {
  setState(`admin.${field}`, value);
}

/**
 * Set admin workflow status with optional error.
 *
 * @param {'idle'|'loading'|'creating'|'deleting'} status - Current status
 * @param {string|null} [error=null] - Error message, or null to clear
 */
export function setAdminStatus(status, error = null) {
  batchUpdate({
    'admin.status': status,
    'admin.error': error
  });
}

/**
 * Select a party for detailed view.
 * Clears any previously loaded API keys.
 *
 * @param {Object} party - Party object to select
 */
export function selectParty(party) {
  batchUpdate({
    'admin.selectedParty': party,
    'admin.selectedPartyKeys': []
  });
}

/**
 * Clear selected party and its keys.
 */
export function clearSelectedParty() {
  batchUpdate({
    'admin.selectedParty': null,
    'admin.selectedPartyKeys': []
  });
}

/**
 * Set the party ID being confirmed for deletion.
 *
 * @param {string} partyId - Party ID to confirm for deletion
 */
export function setDeleteConfirming(partyId) {
  setState('admin.deleteConfirming', partyId);
}

/**
 * Clear delete confirmation state.
 */
export function clearDeleteConfirming() {
  setState('admin.deleteConfirming', null);
}

/**
 * Toggle sort column and direction.
 * If same column, flip direction. Otherwise, set new column with 'asc'.
 *
 * @param {string} column - Column to sort by
 */
export function toggleSort(column) {
  const state = getState();
  const currentColumn = state.admin?.sortColumn;
  const currentDirection = state.admin?.sortDirection;

  // If same column, flip direction; otherwise default to 'asc'
  const newDirection = column === currentColumn
    ? (currentDirection === 'asc' ? 'desc' : 'asc')
    : 'asc';

  batchUpdate({
    'admin.sortColumn': column,
    'admin.sortDirection': newDirection
  });
}

/**
 * Set the active tab for super-admin navigation.
 * Persists to sessionStorage for recovery on page refresh.
 *
 * @param {'admins'|'super_admins'} tab - Tab to activate
 */
export function setActiveTab(tab) {
  setState('admin.activeTab', tab);
  // Persist to sessionStorage for refresh recovery
  try {
    sessionStorage.setItem('dex_super_admin_tab', tab);
  } catch (e) {
    // Ignore storage errors
  }
}

/**
 * Get the persisted active tab from sessionStorage.
 * @returns {'admins'|'super_admins'} The persisted tab or 'admins' default
 */
export function getPersistedActiveTab() {
  try {
    const tab = sessionStorage.getItem('dex_super_admin_tab');
    if (tab === 'admins' || tab === 'super_admins') {
      return tab;
    }
  } catch (e) {
    // Ignore storage errors
  }
  return 'admins';
}

/**
 * Clear all admin state (for cleanup on logout or role switch).
 */
export function clearAdminState() {
  batchUpdate({
    'admin.parties': [],
    'admin.admins': [],
    'admin.superAdmins': [],
    'admin.selectedParty': null,
    'admin.selectedPartyKeys': [],
    'admin.sortColumn': 'party_id',
    'admin.sortDirection': 'asc',
    'admin.activeTab': 'admins',
    'admin.status': 'idle',
    'admin.error': null,
    'admin.deleteConfirming': null
  });
}
